import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { TopCategories } from '@/api/models';
import { CategoryAnalysisSkeleton } from './CategoryAnalysisSkeleton';
import { CategoryAnalysisView } from './CategoryAnalysisView';
import { useCategoryAnalysis } from './useCategoryAnalysis';

type CategoryAnalysisContentProps = {
    data: TopCategories | null;
    error: ErrorResponse | null;
    isLoading: boolean;
};

/**
 * Self-served category-analysis widget — same container / view /
 * skeleton / hook split as the other Sales widgets. Fetches the live
 * `/sales/top-categories` endpoint, scoped to the page-level date range
 * from `useSales()`.
 */
export const CategoryAnalysis: React.FC = () => {
    const { data, error, isLoading, onRetry } = useCategoryAnalysis();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.categoryAnalysis.errorTitle'),
            errorMessage: t('sales.widgets.categoryAnalysis.errorMessage'),
            errorRetry: t('sales.widgets.categoryAnalysis.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="category-analysis-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="category-analysis-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <CategoryAnalysisContent
                data={data}
                error={error}
                isLoading={isLoading}
            />
        </ErrorBoundary>
    );
};

const CategoryAnalysisContent: React.FC<CategoryAnalysisContentProps> = ({
    data,
    error,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return <CategoryAnalysisSkeleton />;
    }

    return <CategoryAnalysisView data={data} />;
};
